import { z } from 'zod';

/**
 * Contato público da landing (`apps/marketing` → `/contact`).
 *
 * Fluxo (CLAUDE.md "Backend é a fonte da verdade"):
 *  1. O form posta para a Function HTTP `submitContactForm`, que valida
 *     na borda com `SubmitContactFormInputSchema` e grava em
 *     `/contactSubmissions/{id}` (status `NEW`). A persistência é o
 *     efeito garantido — o e-mail é secundário.
 *  2. O trigger `onContactSubmissionCreated` envia o lead por e-mail
 *     (vendas@/sales@ conforme locale) e marca `emailSentAt`.
 *  3. O super-admin lê/triagem em `apps/admin` (read gated por Rules) e
 *     muda o status via callable `updateContactSubmissionStatus`.
 *
 * `/whatsappClicks/{id}` guardava os cliques no botão "Falar pelo WhatsApp",
 * gravados pelo beacon `logWhatsappClick`. A landing parou de chamá-lo em
 * 2026-06-19 (a9ac8ec65) e o endpoint saiu em 2026-10-15 (#937); a coleção
 * só tem docs antigos.
 */

/**
 * Locales públicos da landing (6). Espelha `Locale` de
 * `apps/marketing/src/i18n/ui`. Decide o destinatário do e-mail
 * (pt → vendas@; demais → sales@) e o número de WhatsApp.
 */
export const ContactLocaleSchema = z.enum(['pt', 'en', 'es', 'it', 'fr', 'de']);
export type ContactLocale = z.infer<typeof ContactLocaleSchema>;

/**
 * Tipo de operação do lead (opcional). Espelha os segmentos da landing,
 * qualifica o lead para a conversa de vendas.
 */
export const ContactOperationTypeSchema = z.enum([
  'RESTAURANT',
  'BAR',
  'HOTEL',
  'CATERING',
  'DELIVERY',
  'OTHER',
]);
export type ContactOperationType = z.infer<typeof ContactOperationTypeSchema>;
export const ALL_CONTACT_OPERATION_TYPES = ContactOperationTypeSchema.options;

/**
 * Canal pelo qual o lead foi enviado. `FORM` = botão "Enviar mensagem";
 * `WHATSAPP` = botão "Falar pelo WhatsApp" (captura o lead e em seguida abre
 * o WhatsApp com a mensagem pré-montada). Default `FORM` quando ausente.
 */
export const ContactChannelSchema = z.enum(['FORM', 'WHATSAPP']);
export type ContactChannel = z.infer<typeof ContactChannelSchema>;

/** Estado de triagem no admin. `NEW` → `READ` → `ARCHIVED`. */
export const ContactSubmissionStatusSchema = z.enum(['NEW', 'READ', 'ARCHIVED']);
export type ContactSubmissionStatus = z.infer<typeof ContactSubmissionStatusSchema>;
export const ALL_CONTACT_SUBMISSION_STATUSES = ContactSubmissionStatusSchema.options;

/**
 * Input do form público (borda HTTP `submitContactForm`).
 *
 * `website` é honeypot: campo oculto que humanos deixam vazio e bots
 * preenchem. Fica fora da validação estrita (a Function checa o valor
 * cru e responde 200 silencioso quando preenchido, para não sinalizar
 * o bloqueio ao bot).
 */
/**
 * Telefone do lead. Form público internacional (sem tenant), então NÃO
 * vale a trava de país do tenant — aceitamos qualquer DDI. Validação é só
 * de forma: 8–15 dígitos (teto E.164), opcional `+` e separadores comuns
 * (espaço, `-`, `.`, `()`). O `.transform` normaliza para os dígitos
 * (preservando o `+`), que é o que persistimos.
 */
export const ContactPhoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .refine(
    (v) => {
      const digits = v.replace(/[^\d]/g, '');
      return digits.length >= 8 && digits.length <= 15 && /^[+]?[\d\s().-]+$/.test(v);
    },
    { message: 'invalid_phone' },
  )
  .transform((v) => {
    const plus = v.trimStart().startsWith('+') ? '+' : '';
    return plus + v.replace(/[^\d]/g, '');
  });

export const SubmitContactFormInputSchema = z.object({
  /**
   * Id do envio gerado no cliente, também empurrado no `dataLayer`. Existe
   * para deduplicar a conversão: o Pixel do Meta manda o `Lead` do navegador e
   * um envio server-side pelo CAPI mandaria o mesmo evento — sem um id comum,
   * a mesma pessoa contaria como duas conversões. Mesmo contrato do
   * `eventId` de `SubmitLossDiagnosisInputSchema`.
   *
   * `optional`, não obrigatório: a landing é HTML estático em cache, e um
   * visitante com a versão anterior posta sem o campo. Lead sem id vale mais
   * que lead recusado — o que se perde é a dedup, não a conversão.
   */
  eventId: z.string().trim().min(1).max(120).optional(),
  name: z.string().trim().min(2).max(120),
  email: z.email().max(200),
  phone: ContactPhoneSchema,
  message: z.string().trim().min(5).max(4000),
  operationType: ContactOperationTypeSchema.optional(),
  channel: ContactChannelSchema.optional(),
  locale: ContactLocaleSchema,
  website: z.string().max(200).optional(),
  /**
   * Slug da página de segmento de onde o lead veio (`/{locale}/for/<slug>` →
   * `/contact?segment=<slug>`, F6 da #679). É atribuição, não dado do lead:
   * valor fora do formato vira `undefined` em vez de recusar o envio — lead
   * sem segmento vale mais que lead recusado por causa de uma query string.
   */
  segment: z
    .string()
    .regex(/^[a-z0-9-]{1,64}$/)
    .optional()
    .catch(undefined),
});
export type SubmitContactFormInput = z.infer<typeof SubmitContactFormInputSchema>;

/**
 * Documento `/contactSubmissions/{id}`. `createdAt`/`emailSentAt` são
 * Firestore `Timestamp` (tipados `unknown` — o cliente materializa em
 * millis ao ler, como `AuditEventSchema.at`).
 */
export const ContactSubmissionSchema = z.object({
  name: z.string(),
  email: z.string(),
  /**
   * Telefone do lead (dígitos normalizados, com `+` opcional). `nullish`
   * por compat: leads gravados antes deste campo não têm `phone` e ainda
   * precisam parsear no admin (sem migração — leads são imutáveis).
   */
  phone: z.string().nullish(),
  message: z.string(),
  operationType: ContactOperationTypeSchema.nullable(),
  /** Canal de origem. `nullish` por compat com leads anteriores ao campo. */
  channel: ContactChannelSchema.nullish(),
  locale: ContactLocaleSchema,
  status: ContactSubmissionStatusSchema,
  /** E-mail interno destinatário resolvido no servidor (vendas@/sales@). */
  routedTo: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.unknown(),
  /**
   * Id do lead criado no CRM, ou do lead existente ao qual anexamos a nota.
   * `nullish` porque as submissões anteriores ao trigger `crmOnContactSubmissionCreated`
   * não têm o campo, e elas continuam sendo parseadas pelo admin e pelo trigger
   * de e-mail.
   */
  crmLeadId: z.string().nullish(),
  /** Quadro onde o lead caiu — junto com `crmLeadId` monta o link do CRM. */
  crmBoardId: z.string().nullish(),
  /**
   * Id do envio que o cliente também mandou ao Pixel. `nullish` porque as
   * submissões anteriores ao campo não o têm — e porque o próprio input é
   * opcional (ver `SubmitContactFormInputSchema.eventId`).
   */
  eventId: z.string().nullish(),
  /**
   * Página de segmento de origem (ver `SubmitContactFormInputSchema.segment`).
   * `nullish` porque as submissões anteriores ao campo não o têm.
   */
  segment: z.string().nullish(),
  emailSentAt: z.unknown().nullable(),
});
export type ContactSubmission = z.infer<typeof ContactSubmissionSchema>;

/** Input da callable super-admin `updateContactSubmissionStatus`. */
export const UpdateContactSubmissionStatusInputSchema = z.object({
  id: z.string().min(1).max(64),
  status: ContactSubmissionStatusSchema,
});
export type UpdateContactSubmissionStatusInput = z.infer<
  typeof UpdateContactSubmissionStatusInputSchema
>;

/**
 * Documento `/whatsappClicks/{id}` (legado, sem escritor desde a #937 — ver o
 * bloco do módulo). `createdAt` é Firestore `Timestamp`. Fica porque a rule de
 * `whatsappClicks` cita este schema; sai junto com a coleção.
 */
export const WhatsappClickSchema = z.object({
  locale: ContactLocaleSchema,
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  referrer: z.string().nullable(),
  createdAt: z.unknown(),
});
export type WhatsappClick = z.infer<typeof WhatsappClickSchema>;

/**
 * Valores de `crm_leads.source` para lead nascido no formulário de contato do
 * site. `source` é string livre no `CrmLeadSchema`; estas constantes são a
 * convenção, para não espalhar literal pelo código — mesmo papel de
 * `CRM_LEAD_SOURCE_WEBSITE_CALCULATOR`.
 *
 * São DOIS valores porque `channel` distingue quem clicou "Enviar mensagem" de
 * quem clicou "Falar pelo WhatsApp", e essa diferença muda a abordagem do SDR:
 * o segundo abriu um canal síncrono e está esperando resposta agora.
 */
export const CRM_LEAD_SOURCE_WEBSITE_CONTACT = 'WEBSITE_CONTACT';
export const CRM_LEAD_SOURCE_WEBSITE_WHATSAPP = 'WEBSITE_WHATSAPP';
